import type { Namespace } from '@/i18n/types';
import { useCallback } from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';

/**
 * Custom hook for translations that follows the project's hook patterns
 *
 * @param namespace - The translation namespace to use (optional, defaults to 'common')
 * @returns Translation functions and state
 *
 * @example
 * ```typescript
 * // Using common namespace (default)
 * const { t } = useTranslation();
 *
 * // Using specific namespace
 * const { t } = useTranslation('auth');
 *
 * // With interpolation
 * const message = t('messages.showing', { from: 1, to: 10, total: 100 });
 * ```
 */
export function useTranslation(namespace?: Namespace) {
  const { t, i18n, ready } = useI18nextTranslation(namespace || 'common');

  /**
   * A dynamic translation function that safely handles translation keys of any type.
   *
   * This function wraps the standard translation function to provide error handling
   * and fallback behavior when translations fail. It accepts dynamic keys that may
   * not be statically typed, placing the responsibility on the consumer to provide
   * valid translation keys.
   *
   * @param key - Optional runtime translation key
   * @param options - Optional parameters to pass to the translation function (e.g., interpolation values)
   * @returns The translated string if successful, original key as fallback, or empty string when key is missing
   *
   * @example
   * ```typescript
   * const translatedText = tDynamic('common.save');
   * const translatedWithParams = tDynamic('user.greeting', { name: 'John' });
   * const translatedOptional = tDynamic(undefined); // ''
   * ```
   */
  const tDynamic = useCallback(
    (key?: string, options?: Record<string, unknown>): string => {
      if (!key) {
        return '';
      }

      try {
        const translated = options
          ? i18n.t(key, options as never)
          : i18n.t(key as never);
        return typeof translated === 'string' ? translated : String(translated);
      } catch {
        return key; // Fallback to key if translation fails
      }
    },
    [i18n]
  );

  /**
   * Format a date string according to the current language locale
   *
   * @param dateString - The date string to format (ISO format recommended)
   * @param options - Intl.DateTimeFormat options for customization
   * @returns Formatted date string or 'N/A' if invalid/empty
   *
   * @example
   * ```typescript
   * const { formatDate } = useTranslation();
   *
   * // Basic usage
   * const formatted = formatDate('2023-12-25T10:30:00Z');
   *
   * // With custom options
   * const formatted = formatDate('2023-12-25T10:30:00Z', {
   *   dateStyle: 'full',
   *   timeStyle: 'short'
   * });
   * ```
   */
  const formatDate = useCallback(
    (
      dateString?: string | null,
      options?: Intl.DateTimeFormatOptions
    ): string => {
      if (!dateString) {
        return t('notAvailable');
      }

      try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return t('notAvailable');
        }

        // Default options for consistent formatting. Skip the individual
        // date-component defaults when the caller opts into dateStyle/timeStyle,
        // since Intl.DateTimeFormat throws if the two styles are combined.
        const usesStylePreset =
          options?.dateStyle !== undefined || options?.timeStyle !== undefined;
        const defaultOptions: Intl.DateTimeFormatOptions = usesStylePreset
          ? { ...options }
          : {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              ...options,
            };

        // Use the current language for locale-specific formatting
        return new Intl.DateTimeFormat(i18n.language, defaultOptions).format(
          date
        );
      } catch {
        return t('notAvailable');
      }
    },
    [i18n.language, t]
  );

  /**
   * Format a datetime string with full date and time according to the current language locale
   *
   * @param dateString - The date string to format (ISO format recommended)
   * @param options - Optional Intl.DateTimeFormat options for customization
   * @returns Formatted datetime string or 'N/A' if invalid/empty
   *
   * @example
   * ```typescript
   * const { formatDateTime } = useTranslation();
   *
   * // Basic usage - shows full date and time
   * const formatted = formatDateTime('2023-12-25T10:30:00Z');
   * // Result: "25/12/2023, 10:30:00" (format varies by locale)
   * ```
   */
  const formatDateTime = useCallback(
    (
      dateString?: string | null,
      options?: Intl.DateTimeFormatOptions
    ): string => {
      if (!dateString) {
        return t('notAvailable');
      }

      try {
        const date = new Date(dateString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
          return t('notAvailable');
        }

        // Default options for datetime formatting. Skip the individual
        // component defaults when the caller opts into dateStyle/timeStyle,
        // since Intl.DateTimeFormat throws if the two styles are combined.
        const usesStylePreset =
          options?.dateStyle !== undefined || options?.timeStyle !== undefined;
        const defaultOptions: Intl.DateTimeFormatOptions = usesStylePreset
          ? { ...options }
          : {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
              ...options,
            };

        // Use the current language for locale-specific formatting
        return new Intl.DateTimeFormat(i18n.language, defaultOptions).format(
          date
        );
      } catch {
        return t('notAvailable');
      }
    },
    [i18n.language, t]
  );

  /**
   * Format a number according to the current language locale
   *
   * @param number - The number to format
   * @param options - Intl.NumberFormat options for customization
   * @returns Formatted number string or 'N/A' if invalid/null
   *
   * @example
   * ```typescript
   * const { formatNumber } = useTranslation();
   *
   * // Basic usage
   * const formatted = formatNumber(1234.56);
   *
   * // With custom options
   * const formatted = formatNumber(1234.56, {
   *   style: 'currency',
   *   currency: 'EUR'
   * });
   *
   * // Percentage
   * const formatted = formatNumber(0.1234, {
   *   style: 'percent',
   *   minimumFractionDigits: 2
   * });
   * ```
   */
  const formatNumber = useCallback(
    (number?: number | null, options?: Intl.NumberFormatOptions): string => {
      if (number === null || number === undefined || isNaN(number)) {
        return String(number);
      }

      try {
        // Default options for consistent formatting
        const defaultOptions: Intl.NumberFormatOptions = {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
          ...options,
        };

        // Use the current language for locale-specific formatting
        return new Intl.NumberFormat(i18n.language, defaultOptions).format(
          number
        );
      } catch {
        return t('notAvailable');
      }
    },
    [i18n.language, t]
  );

  return {
    /** Translation function */
    t,
    /** Translation function for dynamic keys */
    tDynamic,
    /** Format date according to current language locale */
    formatDate,
    /** Format datetime according to current language locale */
    formatDateTime,
    /** Format number according to current language locale */
    formatNumber,
    /** Current language */
    language: i18n.language,
    /** Change language */
    changeLanguage: i18n.changeLanguage,
    /** Check if translations are loaded */
    isReady: ready,
    /** Check if currently loading */
    isLoading: !ready,
    /** i18n instance for advanced usage */
    i18n,
  };
}

/**
 * Hook for changing language with type safety
 */
export function useLanguage() {
  const { language, changeLanguage, isLoading } = useTranslation();

  const setLanguage = async (newLanguage: string) => {
    try {
      await changeLanguage(newLanguage);
      // Store in localStorage for persistence
      localStorage.setItem('i18nextLng', newLanguage);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  return {
    currentLanguage: language,
    setLanguage,
    isLoading,
  };
}
